import React, { useState, useEffect, useRef } from 'react';
import { FiUpload, FiTrash2, FiPlay, FiPause, FiMusic, FiStar } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function MusicManagement() {
  const [files, setFiles] = useState({ welcome: [], hold: [], off_hours: [], csat: {} });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ welcome: false, hold: false, off_hours: false, csat_prompt: false, csat_thanks: false });
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const welcomeInputRef = useRef(null);
  const holdInputRef = useRef(null);
  const offHoursInputRef = useRef(null);
  const csatPromptRef = useRef(null);
  const csatThanksRef = useRef(null);

  const token = localStorage.getItem('token');

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/music/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFiles(data);
      }
    } catch (err) {
      toast.error("Ro'yxatni olishda xato");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleUpload = async (type, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));

    const formData = new FormData();
    formData.append('audio', file);

    try {
      const res = await fetch(`/api/music/upload/${type}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(type === 'welcome' ? 'Welcome musiqa yuklandi' : 'Kutish musiqasi yuklandi');
        fetchFiles();
      } else {
        toast.error(data.error || 'Yuklashda xato');
      }
    } catch (err) {
      toast.error('Yuklashda xato');
    }
    setUploading(prev => ({ ...prev, [type]: false }));
  };

  const handleDelete = async (type, filename) => {
    if (!window.confirm(`"${filename}" o'chirilsinmi?`)) return;

    try {
      const res = await fetch(`/api/music/${type}/${filename}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("O'chirildi");
        fetchFiles();
      } else {
        toast.error("O'chirishda xato");
      }
    } catch (err) {
      toast.error("O'chirishda xato");
    }
  };

  const handlePlay = (type, filename) => {
    const key = `${type}/${filename}`;
    if (playing === key) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlaying(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(`/api/music/file/${type}/${filename}`);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      toast.error('Tinglab bo\'lmadi');
      setPlaying(null);
    };
    audio.play();
    audioRef.current = audio;
    setPlaying(key);
  };

  // === CSAT baholash ovozlari ===
  const handleCsatUpload = async (kind, file) => {
    if (!file) return;
    const ukey = `csat_${kind}`;
    setUploading(prev => ({ ...prev, [ukey]: true }));
    const formData = new FormData();
    formData.append('audio', file);
    try {
      const res = await fetch(`/api/music/csat/${kind}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (res.ok) { toast.success('Baholash ovozi yuklandi'); fetchFiles(); }
      else toast.error(data.error || 'Yuklashda xato');
    } catch (err) { toast.error('Yuklashda xato'); }
    setUploading(prev => ({ ...prev, [ukey]: false }));
  };

  const handleCsatDelete = async (kind) => {
    if (!window.confirm("O'chirilsinmi?")) return;
    try {
      const res = await fetch(`/api/music/csat/${kind}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success("O'chirildi"); fetchFiles(); }
      else toast.error("O'chirishda xato");
    } catch (err) { toast.error("O'chirishda xato"); }
  };

  const handleCsatPlay = (kind) => {
    const key = `csat/${kind}`;
    if (playing === key) { if (audioRef.current) audioRef.current.pause(); setPlaying(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(`/api/music/csat/${kind}/file`);
    audio.onended = () => setPlaying(null);
    audio.onerror = () => { toast.error('Tinglab bo\'lmadi'); setPlaying(null); };
    audio.play();
    audioRef.current = audio;
    setPlaying(key);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderSection = (type, title, description, allowMultiple) => {
    const list = files[type] || [];
    const inputRef = type === 'welcome' ? welcomeInputRef : type === 'off_hours' ? offHoursInputRef : holdInputRef;

    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionTitle}>
              <FiMusic size={18} />
              {title}
            </div>
            <div style={styles.sectionDesc}>{description}</div>
          </div>
          <button
            style={styles.uploadBtn}
            onClick={() => inputRef.current?.click()}
            disabled={uploading[type]}
          >
            <FiUpload size={14} />
            {uploading[type] ? 'Yuklanmoqda...' : 'Yuklash'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleUpload(type, e.target.files[0]);
                e.target.value = '';
              }
            }}
          />
        </div>

        {list.length === 0 ? (
          <div style={styles.empty}>
            {type === 'welcome' 
              ? 'Welcome musiqa yuklanmagan. Qo\'ng\'iroq kelganda standart ovoz ishlatiladi.'
              : 'Kutish musiqasi yuklanmagan. Navbatda kutayotganlar sukut tinglaydi.'}
          </div>
        ) : (
          <div style={styles.fileList}>
            {list.map(file => (
              <div key={file.name} style={styles.fileRow}>
                <button
                  style={styles.playBtn}
                  onClick={() => handlePlay(type, file.name)}
                >
                  {playing === `${type}/${file.name}` ? <FiPause size={14} /> : <FiPlay size={14} />}
                </button>
                <div style={styles.fileInfo}>
                  <div style={styles.fileName}>{file.name}</div>
                  <div style={styles.fileSize}>{formatSize(file.size)}</div>
                </div>
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(type, file.name)}
                  title="O'chirish"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCsatRow = (kind, title, desc, inputRef, required) => {
    const f = (files.csat || {})[kind];
    const ukey = `csat_${kind}`;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={{ ...styles.sectionTitle, fontSize: 14 }}>
              {title}{required && <span style={{ color: '#ef4444' }}> *</span>}
            </div>
            <div style={styles.sectionDesc}>{desc}</div>
          </div>
          <button style={styles.uploadBtn} onClick={() => inputRef.current?.click()} disabled={uploading[ukey]}>
            <FiUpload size={14} />
            {uploading[ukey] ? 'Yuklanmoqda...' : (f ? 'Almashtirish' : 'Yuklash')}
          </button>
          <input ref={inputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files && e.target.files[0]) { handleCsatUpload(kind, e.target.files[0]); e.target.value = ''; } }} />
        </div>
        {f ? (
          <div style={styles.fileRow}>
            <button style={styles.playBtn} onClick={() => handleCsatPlay(kind)}>
              {playing === `csat/${kind}` ? <FiPause size={14} /> : <FiPlay size={14} />}
            </button>
            <div style={styles.fileInfo}>
              <div style={styles.fileName}>{f.name}</div>
              <div style={styles.fileSize}>{formatSize(f.size)}</div>
            </div>
            <button style={styles.deleteBtn} onClick={() => handleCsatDelete(kind)} title="O'chirish"><FiTrash2 size={14} /></button>
          </div>
        ) : (
          <div style={styles.empty}>{required ? 'Hali yuklanmagan — baholash to\'liq ishlashi uchun majburiy.' : 'Ixtiyoriy.'}</div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Musiqa va tovushlar</h2>
        <p style={styles.subtitle}>Kiruvchi qo'ng'iroqlar uchun audio sozlamalari</p>
      </div>

      {renderSection(
        'welcome',
        'Xush kelibsiz xabari',
        "Qo'ng'iroq kelgan zahoti bir marta o'ynatiladi (1 ta fayl)",
        false
      )}

      {renderSection(
        'hold',
        'Kutish musiqasi',
        'Navbatda kutayotganlar uchun (bir nechta fayl bo\'lishi mumkin)',
        true
      )}

      {renderSection(
        'off_hours',
        'Ish vaqtidan tashqari',
        'Ish vaqtidan tashqarida keladigan qo\'ng\'iroqlar uchun (1 ta fayl)',
        false
      )}

      {/* CSAT baholash ovozlari */}
      <div style={styles.section}>
        <div style={{ ...styles.sectionTitle, marginBottom: 4 }}>
          <FiStar size={18} /> Baholash ovozlari (CSAT)
        </div>
        <div style={{ ...styles.sectionDesc, marginBottom: 16 }}>
          Qo'ng'iroq tugagach mijoz xizmatni 1–5 baholaydi. Quyidagi ovozli xabar o'ynatiladi.
        </div>
        {renderCsatRow('prompt', 'Baholash so\'rovi',
          'Masalan: "Xizmatimizni 1 dan 5 gacha baholang. 5 — a\'lo, 1 — yomon. Tugmani bosing."',
          csatPromptRef, true)}
        {renderCsatRow('thanks', 'Rahmat xabari',
          'Baho berilgach o\'ynatiladi. Masalan: "Bahoyingiz uchun rahmat!" (ixtiyoriy)',
          csatThanksRef, false)}
      </div>

      <div style={styles.note}>
        <strong>Eslatma:</strong> Yuklanadigan fayllar avtomatik ravishda Asterisk formatiga (WAV 8kHz mono) 
        konvertatsiya qilinadi. Qo'llab-quvvatlanadigan formatlar: MP3, WAV, OGG, M4A, AAC, FLAC. 
        Maksimal hajm: 20 MB.
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 24 },
  header: { marginBottom: 32 },
  title: {
    fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0,
  },
  subtitle: {
    fontSize: 14, color: '#94a3b8', marginTop: 4,
  },
  section: {
    background: '#1e293b', border: '1px solid #2d3a4f',
    borderRadius: 12, padding: 20, marginBottom: 20,
  },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 16, gap: 12,
  },
  sectionTitle: {
    fontSize: 16, fontWeight: 700, color: '#f1f5f9',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  sectionDesc: {
    fontSize: 13, color: '#94a3b8', marginTop: 4,
  },
  uploadBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', background: '#3b82f6', border: 'none',
    borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  empty: {
    padding: 20, textAlign: 'center', color: '#64748b',
    fontSize: 13, background: '#0f172a', borderRadius: 8,
  },
  fileList: {
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: 12, background: '#0f172a', borderRadius: 8,
    border: '1px solid #2d3a4f',
  },
  playBtn: {
    width: 32, height: 32, borderRadius: '50%',
    background: '#3b82f6', border: 'none', color: 'white',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    fontSize: 14, fontWeight: 600, color: '#f1f5f9',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  fileSize: {
    fontSize: 12, color: '#94a3b8', marginTop: 2,
  },
  deleteBtn: {
    width: 32, height: 32, borderRadius: 6,
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  note: {
    padding: 16, background: 'rgba(59,130,246,0.1)',
    border: '1px solid rgba(59,130,246,0.3)',
    borderRadius: 8, fontSize: 13, color: '#cbd5e1',
    lineHeight: 1.6,
  },
};
